import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BACKEND_URL } from "@/lib/backendUrl";
import { stageOf, fmtShort, money } from "@/hooks/useCalendarJobs";
import { PaidBadge } from "@/components/calendar/JobCard";

const API = `${BACKEND_URL}/api`;
const Row = ({ l, v, testid }) => <div className="flex justify-between gap-3 text-sm py-1 border-b border-slate-800/70" data-testid={testid}><span className="text-slate-500">{l}</span><span className="text-slate-100 text-right">{v || "—"}</span></div>;
const H = ({ children }) => <h4 className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mt-4 mb-1">{children}</h4>;
const Field = ({ label, children }) => <label className="block text-xs text-slate-400 space-y-1"><span>{label}</span>{children}</label>;
const inp = "h-9 bg-slate-950/60 border-slate-700 text-sm";
const formFromJob = (j) => ({ stage: j.stage, scheduled_date: j.scheduled_date || "", expected_done: j.expected_done || "", job_notes: j.job_notes || "", total: j.total ?? 0 });
const isPriced = (j) => !!(j?.invoice_id || j?.line_items?.length || j?.services?.length || j?.parts?.length);

// State + actions for the drawer (load, save, approve). Owned by JobDrawer only.
function useJobDrawer(jobId, { onClose, onChanged, onCollected }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [approving, setApproving] = useState(false);
  useEffect(() => {
    if (!jobId) { setData(null); return; }
    axios.get(`${API}/jobs/${jobId}`).then(({ data: d }) => { setData(d); setForm(formFromJob(d.job)); }).catch(() => toast.error("Job not found"));
  }, [jobId]);
  const j = data?.job;
  const priced = isPriced(j);
  const reload = async () => { const { data: d } = await axios.get(`${API}/jobs/${jobId}`); setData(d); return d; };
  const save = async () => {
    const body = { ...form, expected_done: form.expected_done || null, total: priced ? undefined : Number(form.total || 0), stage: j?.status === "draft" ? undefined : form.stage };
    try {
      const { data: r } = await axios.patch(`${API}/jobs/${jobId}`, body);
      toast.success("Job updated", { description: r.invoice_created ? `Invoice ${r.invoice_created} created · 已开票` : undefined });
      onChanged?.();
      if (body.stage === "collected" && j.stage !== "collected") { onClose(); onCollected?.(r); }
      await reload();
    } catch (e) { toast.error("Could not save", { description: e?.response?.data?.detail }); }
  };
  const approve = async () => {
    setApproving(true);
    try {
      const { data: q } = await axios.patch(`${API}/quotes/${jobId}/status`, { status: "approved" });
      const monthly = data?.customer?.billing_cycle === "monthly";
      if (!q.invoice_no && !monthly) {
        try { const { data: inv } = await axios.post(`${API}/quotes/${jobId}/invoice`, {}); toast.success(`Approved · invoice ${inv.invoice_no} created`, { description: `Due ${inv.due_date} · 已确认并开票` }); }
        catch (e) { toast.error("Approved, but invoice not created", { description: e?.response?.data?.detail }); }
      } else {
        toast.success("Quote approved · 已确认", { description: monthly ? "Billed on the customer's monthly invoice" : undefined });
      }
      onChanged?.();
      const d = await reload();
      setForm((f) => ({ ...f, stage: d.job.stage, expected_done: d.job.expected_done || "" }));
    } catch (e) { toast.error("Could not approve", { description: e?.response?.data?.detail }); }
    finally { setApproving(false); }
  };
  return { data, j, form, setForm, priced, approving, save, approve };
}

function DrawerHeader({ j, st, paid }) {
  return (
    <SheetHeader>
      <SheetTitle className="text-slate-100 flex items-center gap-3 flex-wrap"><span className="font-mono-num text-2xl">{j.plate || j.quote_no}</span>
        <span className="text-xs font-bold rounded-full px-2 py-0.5" style={{ background: `${st.colour}22`, color: st.colour, border: `1px solid ${st.colour}66` }} data-testid="drawer-stage-badge">{st.name}</span>
        <PaidBadge paid={paid} draft={j.status === "draft"} id="drawer" /></SheetTitle>
      <SheetDescription className="text-sm text-slate-400">{j.vehicle || "Vehicle"} · {j.quote_no}{j.demo ? " · DEMO" : ""}</SheetDescription>
    </SheetHeader>
  );
}

function VehicleCustomer({ j, data }) {
  return (
    <>
      <H>Vehicle 车辆</H>
      <Row l="Registration" v={j.plate} /><Row l="Make / Model" v={data.vehicle?.make_model || j.vehicle} />
      <H>Customer 客户</H>
      <Row l="Name" v={data.customer ? <Link to={`/app?edit=${data.customer.id}`} className="text-amber-300 hover:underline">{data.customer.name}</Link> : j.customer_name} /><Row l="Phone" v={data.customer?.phone || j.customer_phone} />
    </>
  );
}

function StageField({ j, config, form, setForm, approving, approve }) {
  if (j.status === "draft") {
    return <Field label="Stage"><Button onClick={approve} disabled={approving} data-testid="drawer-approve-btn" className="w-full h-9 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs">{approving ? "Approving…" : "Approve quote · 确认"}</Button></Field>;
  }
  return (
    <Field label="Stage"><div className="flex rounded-md border border-slate-700 bg-slate-950/60 p-0.5">{(config.job_stages || []).map((s) => <button key={s.key} type="button" onClick={() => setForm({ ...form, stage: s.key })} data-on={form.stage === s.key} data-testid={`drawer-stage-${s.key}`} className="flex-1 h-8 rounded text-[11px] font-bold text-slate-400 data-[on=true]:text-slate-950" style={form.stage === s.key ? { background: s.colour } : undefined}>{s.name.split(" ")[0]}</button>)}</div></Field>
  );
}

function JobForm({ j, data, config, form, setForm, priced, approving, approve, save }) {
  return (
    <>
      <H>Job 工作</H>
      <div className="grid grid-cols-2 gap-2 mt-1">
        <StageField j={j} config={config} form={form} setForm={setForm} approving={approving} approve={approve} />
        <Field label="Final amount · 金额"><Input type="number" min="0" step="0.01" value={form.total} disabled={priced} onChange={(e) => setForm({ ...form, total: e.target.value })} data-testid="drawer-total" className={`${inp} font-mono-num`} /></Field>
        <Field label="Scheduled"><Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} data-testid="drawer-scheduled" className={`${inp} [color-scheme:dark]`} /></Field>
        <Field label="Est. completion"><Input type="date" value={form.expected_done} onChange={(e) => setForm({ ...form, expected_done: e.target.value })} data-testid="drawer-expected" className={`${inp} [color-scheme:dark]`} /></Field>
      </div>
      {priced && <p className="text-[11px] text-slate-500 mt-1">{j.invoice_id ? `Amount fixed by invoice ${j.invoice_no}.` : "Amount comes from the quote calculator — edit the quote to change it."}</p>}
      <Field label="Notes"><textarea value={form.job_notes} onChange={(e) => setForm({ ...form, job_notes: e.target.value })} data-testid="drawer-notes" rows={2} className="w-full rounded-md bg-slate-950/60 border border-slate-700 p-2 text-sm text-slate-100" /></Field>
      {j.status === "draft" && <p className="text-[11px] text-slate-500 mt-1">Approving starts the job on its scheduled day{data?.customer?.billing_cycle === "monthly" ? " (billed monthly)" : " and creates the invoice"}. <Link to={`/quotes?q=${encodeURIComponent(j.quote_no || j.plate || "")}`} className="text-amber-300 hover:underline" data-testid="drawer-approve-link">Edit quote →</Link></p>}
      <Button onClick={save} data-testid="drawer-save" className="mt-3 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold">Save job · 保存</Button>
    </>
  );
}

function PaymentSection({ j, data, paid }) {
  return (
    <>
      <H>Payment 付款</H>
      <Row l="Final amount" v={<span className="font-mono-num font-bold text-amber-300">{money(j.total)}</span>} testid="drawer-amount" />
      <Row l="Status" v={paid ? <span className="text-emerald-300">Paid{data.invoice?.paid_at ? ` · ${fmtShort(data.invoice.paid_at.slice(0, 10))}` : ""}</span> : <span className="text-rose-300">Unpaid</span>} testid="drawer-payment" />
      <Row l="Quotation" v={<Link to={`/quotes?q=${j.quote_no}`} className="text-amber-300 hover:underline">{j.quote_no}</Link>} />
      <Row l="Invoice" v={data.invoice ? <Link to="/invoices" className="text-sky-300 hover:underline">{data.invoice.invoice_no} · {data.invoice.status}</Link> : "not invoiced"} />
    </>
  );
}

function ActivityList({ activity }) {
  const items = activity || [];
  return (
    <>
      <H>Activity 记录</H>
      <ol className="space-y-1.5 text-xs" data-testid="drawer-activity">
        {[...items].reverse().map((a, i) => (
          <li key={`${a.at}-${i}`} className="flex gap-2"><span className="font-mono-num text-slate-500 shrink-0">{new Date(a.at).toLocaleString("en-SG", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
            <span className="text-slate-300">{a.action}{a.old || a.new ? <span className="text-slate-500"> · {a.old ?? "—"} → {a.new ?? "—"}</span> : null} <span className="text-slate-600">· {a.user}</span></span></li>
        ))}
        {!items.length && <li className="text-slate-600">No changes recorded yet</li>}
      </ol>
    </>
  );
}

// Right-side drawer: vehicle · customer · stage/dates · final amount · paid/unpaid · activity
export default function JobDrawer({ jobId, config, onClose, onChanged, onCollected }) {
  const { data, j, form, setForm, priced, approving, save, approve } = useJobDrawer(jobId, { onClose, onChanged, onCollected });
  const st = j ? stageOf(config, j.stage) : null;
  const paid = j?.status === "paid";
  return (
    <Sheet open={!!jobId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-[#111827] border-slate-700 text-slate-100 overflow-y-auto" data-testid="job-drawer">
        {!j && <SheetHeader><SheetTitle className="text-slate-100">Loading job…</SheetTitle><SheetDescription>Job details</SheetDescription></SheetHeader>}
        {j && (
          <>
            <DrawerHeader j={j} st={st} paid={paid} />
            <VehicleCustomer j={j} data={data} />
            <JobForm j={j} data={data} config={config} form={form} setForm={setForm} priced={priced} approving={approving} approve={approve} save={save} />
            <PaymentSection j={j} data={data} paid={paid} />
            <ActivityList activity={j.activity} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
